import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { RefreshCw, Zap, BookOpen, Layers, Code, ChevronLeft, ChevronRight, RotateCcw, LayoutList, Puzzle, Feather, Waypoints, Target, Check, X, SkipForward, TrendingUp, Calendar, Award, Clock, Edit3, Save, Volume2, Trophy, Flame, BarChart3, Play, MessageSquare, Shuffle } from 'lucide-react';

// — UTILITY: Storage Hook —
const useStorageState = (key, defaultValue) => {
const [state, setState] = useState(() => {
try {
const stored = localStorage.getItem(key);
return stored ? JSON.parse(stored) : defaultValue;
} catch {
return defaultValue;
}
});

useEffect(() => {
try {
localStorage.setItem(key, JSON.stringify(state));
} catch (e) {
console.error('Storage error:', e);
}
}, [key, state]);

return [state, setState];
};

// — APPLICATION DATA - TWO ORGANIZATIONAL MODES —

// Helper function to create a single term object
const T = (term, definition, related = []) => ({ term, definition, related });

// — DEFINITIONS (Master List of Concepts) —
const DEFINITIONS = {
// P1: UI & State Essentials (Core Flutter)
Text: T("Text", "The fundamental widget for displaying a string of text on the screen. (VERY HIGH USE)"),
Container: T("Container", "A convenience widget that combines common painting, positioning, and sizing (like a versatile box). (VERY HIGH USE)"),
Scaffold: T("Scaffold", "Provides the basic visual structure for a Material Design app (appBar, body, floatingActionButton, etc.). (VERY HIGH USE)"),
Row: T("Row", "A layout widget that arranges its children horizontally.", ["Column"]),
Column: T("Column", "A layout widget that arranges its children vertically.", ["Row"]),
MaterialApp: T("MaterialApp", "A convenience widget that wraps a number of widgets commonly required for mobile apps (theme, navigation, etc.). It’s the root of most Flutter apps."),
StatelessWidget: T("StatelessWidget", "A widget that describes part of the UI that does NOT change dynamically (it has no mutable internal state). (HIGH USE)", ["StatefulWidget"]),
StatefulWidget: T("StatefulWidget", "A widget that describes part of the UI that CAN change dynamically (it manages mutable internal state). (HIGH USE)", ["StatelessWidget"]),
Widget: T("Widget", "The basic building block of a Flutter app. Everything in Flutter is a widget (layout, text, buttons, etc.)."),

// P2: Dart Code Fundamentals (Core Dart)
main: T("main()", "The top-level function where all Dart programs (including Flutter apps) begin execution. (VERY HIGH USE)"),
var: T("var", "A keyword used for variables where the type is inferred (determined) by the initial value. (VERY HIGH USE)", ["final", "const"]),
int: T("int", "A 64-bit integer (whole number). Example: `int age = 30;` (VERY HIGH USE)", ["double"]),
String: T("String", "A sequence of UTF-16 code units (text). Example: `String name = 'Alice';` (VERY HIGH USE)"),
bool: T("bool", "A boolean value, either `true` or `false`. Essential for logic. (VERY HIGH USE)"),
final: T("final", "A variable that can only be set once. Its value is determined at runtime, once it is first used. (HIGH USE)", ["const", "var"]),
const: T("const", "A compile-time constant. Its value must be known before the program starts running. (HIGH USE)", ["final", "var"]),
if_else: T("if / else", "Conditional logic to execute different code blocks based on a `bool` condition."),
void: T("void", "Indicates that a function does not return any value. Example: `void printHello() {...}`"),
double: T("double", "A 64-bit floating-point number (with decimals). Example: `double price = 19.99;`", ["int"]),

// P3: Layout and Styling
AppBar: T("AppBar", "A Material Design app bar that is typically the first widget in a `Scaffold`."),
Padding: T("Padding", "A widget that adds empty space around its child widget."),
Center: T("Center", "A layout widget that centers its child within the available space."),
SizedBox: T("SizedBox", "Used to create a box with a specific size or to add space between widgets."),
Image: T("Image", "Widget for displaying images from assets, network, or files."),
Icon: T("Icon", "Widget for displaying Material Design vector icons."),
ListView: T("ListView", "A scrollable list of widgets arranged linearly. Essential for displaying dynamic or numerous items."),
Expanded: T("Expanded", "Forces a `Row` or `Column` child to take up all the available space along the main axis.", ["Flexible"]),
Flexible: T("Flexible", "Allows a `Row` or `Column` child to resize, but not necessarily fill all available space.", ["Expanded"]),

// P4: Interaction & Data Handling
setState: T("setState()", "A method called within a `State` object to tell Flutter that the internal state has changed and the UI must be rebuilt. (HIGH USE)"),
onPressed: T("onPressed", "The required function property for most button widgets (like `ElevatedButton`) that defines what happens when the button is tapped. (HIGH USE)"),
ElevatedButton: T("ElevatedButton", "A Material Design button that lifts off the surface when pressed."),
TextField: T("TextField", "An interactive widget that allows the user to input text."),
Navigator: T("Navigator", "A widget that manages a stack of widgets (routes). Used for moving between screens."),
List: T("List", "An ordered collection of objects (like an array). Example: `List<String> names = ['A', 'B'];` (MEDIUM USE)"),
Map: T("Map", "An unordered collection of key-value pairs. Example: `Map<String, int> scores = {'A': 90};` (MEDIUM USE)"),
GestureDetector: T("GestureDetector", "A non-visual widget used to detect user input gestures (like taps, drags) on its child."),
http_package: T("http package", "The standard Dart package used to perform HTTP requests (GET, POST, etc.) to fetch data from REST APIs."),

// P5: Dart OOP & Advanced Concepts
class: T("class", "The blueprint for creating objects, containing properties (data) and methods (functions)."),
this: T("this", "Used to refer to the current instance of the class (the object itself). Often used for constructor shorthand."),
super: T("super", "Used inside a subclass to refer to the immediate parent class, often to call the parent’s constructor."),
extends: T("extends", "Used to create a subclass that inherits properties and methods from a superclass (parent class)."),
late: T("late", "Declares a non-nullable variable that must be initialized before its first use, but not necessarily when it’s declared."),
null_safety: T("null safety (?)", "Dart’s system to prevent accidental `null` exceptions. `String?` means the string can be `null` or hold a `String`."),
implements: T("implements", "Used to promise that a class will provide all the methods and properties defined in an interface (another class)."),
Set: T("Set", "An unordered collection of unique elements (no duplicates). Example: `Set<int> ids = {1, 2, 3};`"),
map_where: T(".map()/.where()", "Collection methods for transforming and filtering lists (e.g., creating a new list from an old one)."),
Provider: T("Provider", "A popular, simple state management solution used to expose state/data to the widget tree and manage app-wide state."),
Firebase: T("Firebase", "A comprehensive app development platform from Google that provides backend services like Authentication and Databases."),
Firestore: T("Firestore", "A flexible, cloud-hosted, NoSQL database (part of Firebase) used for storing and syncing data in real-time."),

// P6: Asynchronous, Lifecycle, & Environment
async_await: T("async/await", "Keywords used together to handle asynchronous operations cleanly, making code look synchronous. (MEDIUM USE)"),
Future: T("Future", "An object representing a potential value or error that will be available at some point in the future. (MEDIUM USE)"),
initState: T("initState()", "A lifecycle method on `State` called only once when the widget is first created and inserted into the tree."),
dispose: T("dispose()", "A lifecycle method on `State` called when the widget is permanently removed from the tree, used to clean up resources (listeners, controllers)."),
BuildContext: T("BuildContext", "A handle to the location of a widget in the widget tree. Used to access themes, media queries, or the `Navigator`."),
pubspec_yaml: T("pubspec.yaml", "The configuration file for a Flutter project, defining dependencies (packages), assets, and metadata."),
HotReload: T("Hot Reload", "Flutter feature that quickly injects updated source code files into the running app, preserving the app state. Used for fast UI iteration."),
Key: T("Key", "An identifier used by Flutter to uniquely identify stateful widgets, crucial for performance and preserving state across rebuilds."),
Riverpod: T("Riverpod", "A modern, compile-safe, and highly flexible reimplementation of the Provider package, popular for advanced apps."),
BLoC: T("BLoC", "Business Logic Component. A powerful, but complex, state management pattern that uses Streams to separate business logic from the UI."),
WidgetTesting: T("Widget Testing", "Used to verify that a single widget’s UI looks and interacts correctly (simulating user actions)."),
UnitTesting: T("Unit Testing", "Used to verify the smallest testable parts of an application’s logic (Dart code), independent of the UI."),
DevTools: T("DevTools", "A suite of performance and debugging tools provided by Flutter for profiling, inspecting the widget tree, and diagnosing issues."),
};

// Map all definitions to initial progress state
const getInitialCardStats = () => Object.keys(DEFINITIONS).reduce((acc, term) => {
acc[term] = {
term: DEFINITIONS[term].term,
definition: DEFINITIONS[term].definition,
related: DEFINITIONS[term].related || [],
wrongCount: 0,
correctStreak: 0,
lastReviewed: null,
nextReview: Date.now(), // Available immediately
reviewCount: 0,
personalNote: '',
personalExample: '',
easeFactor: 2.5, // SRS ease factor
};
return acc;
}, {});

// — ORGANIZATION 1: BY FREQUENCY (Existing Priority Roadmap) —
const CONCEPTS_BY_FREQUENCY = [
{
category: "P1: UI & State Essentials (Most Used)",
icon: <Layers size={18} />,
terms: [
DEFINITIONS.Text, DEFINITIONS.Container, DEFINITIONS.Scaffold,
DEFINITIONS.Row, DEFINITIONS.Column, DEFINITIONS.StatelessWidget,
DEFINITIONS.StatefulWidget, DEFINITIONS.Widget,
]
},
{
category: "P2: Dart Code Fundamentals",
icon: <Code size={18} />,
terms: [
DEFINITIONS.main, DEFINITIONS.var, DEFINITIONS.int, DEFINITIONS.String,
DEFINITIONS.bool, DEFINITIONS.final, DEFINITIONS.const, DEFINITIONS.if_else,
DEFINITIONS.void, DEFINITIONS.double,
]
},
{
category: "P3: Layout & Styling (Making it Look Right)",
icon: <LayoutList size={18} />,
terms: [
DEFINITIONS.AppBar, DEFINITIONS.Padding, DEFINITIONS.Center, DEFINITIONS.SizedBox,
DEFINITIONS.Image, DEFINITIONS.Icon, DEFINITIONS.ListView, DEFINITIONS.Expanded,
DEFINITIONS.Flexible,
]
},
{
category: "P4: Interaction, Basic State & Data Handling",
icon: <Puzzle size={18} />,
terms: [
DEFINITIONS.setState, DEFINITIONS.onPressed, DEFINITIONS.ElevatedButton,
DEFINITIONS.TextField, DEFINITIONS.Navigator, DEFINITIONS.List,
DEFINITIONS.Map, DEFINITIONS.GestureDetector, DEFINITIONS.initState,
]
},
{
category: "P5: OOP & Advanced Data (Architecture)",
icon: <Target size={18} />,
terms: [
DEFINITIONS.class, DEFINITIONS.this, DEFINITIONS.super, DEFINITIONS.extends,
DEFINITIONS.late, DEFINITIONS.null_safety, DEFINITIONS.implements,
DEFINITIONS.Set, DEFINITIONS.map_where, DEFINITIONS.async_await, DEFINITIONS.Future,
]
},
{
category: "P6: Advanced Tools & Backend (Least Used)",
icon: <Feather size={18} />,
terms: [
DEFINITIONS.Provider, DEFINITIONS.Firebase, DEFINITIONS.Firestore, DEFINITIONS.http_package,
DEFINITIONS.Riverpod, DEFINITIONS.BLoC, DEFINITIONS.WidgetTesting, DEFINITIONS.DevTools,
DEFINITIONS.dispose, DEFINITIONS.BuildContext, DEFINITIONS.pubspec_yaml, DEFINITIONS.HotReload,
DEFINITIONS.Key, DEFINITIONS.UnitTesting
]
},
];

// — ORGANIZATION 2: BY CAREER ROADMAP (Structured Learning Path - Google Keep) —
const CONCEPTS_BY_ROADMAP = [
{
category: "Phase 1: Dart Fundamentals (The Foundation)",
icon: <Code size={18} />,
terms: [
DEFINITIONS.main, DEFINITIONS.int, DEFINITIONS.double, DEFINITIONS.String,
DEFINITIONS.bool, DEFINITIONS.var, DEFINITIONS.final, DEFINITIONS.const,
DEFINITIONS.void, DEFINITIONS.if_else, DEFINITIONS.List, DEFINITIONS.Map,
DEFINITIONS.class, DEFINITIONS.this, DEFINITIONS.extends, DEFINITIONS.super,
DEFINITIONS.implements, DEFINITIONS.async_await, DEFINITIONS.Future, DEFINITIONS.late,
DEFINITIONS.null_safety,
]
},
{
category: "Phase 2: Flutter UI & Core Widgets (Building the Screen)",
icon: <Layers size={18} />,
terms: [
DEFINITIONS.Widget, DEFINITIONS.StatelessWidget, DEFINITIONS.StatefulWidget,
DEFINITIONS.MaterialApp, DEFINITIONS.Scaffold, DEFINITIONS.HotReload,
DEFINITIONS.Container, DEFINITIONS.Text, DEFINITIONS.Row, DEFINITIONS.Column,
DEFINITIONS.ListView, DEFINITIONS.AppBar, DEFINITIONS.Padding, DEFINITIONS.SizedBox,
DEFINITIONS.Center, DEFINITIONS.Image, DEFINITIONS.Icon, DEFINITIONS.Navigator,
DEFINITIONS.BuildContext, DEFINITIONS.setState, DEFINITIONS.onPressed, DEFINITIONS.initState,
DEFINITIONS.dispose,
]
},
{
category: "Phase 3: Advanced State, Data, and Architecture",
icon: <Puzzle size={18} />,
terms: [
DEFINITIONS.http_package, DEFINITIONS.List, DEFINITIONS.Map, DEFINITIONS.map_where,
DEFINITIONS.async_await, DEFINITIONS.Future, DEFINITIONS.Provider,
DEFINITIONS.Firebase, DEFINITIONS.Firestore, DEFINITIONS.pubspec_yaml,
]
},
{
category: "Phase 6: Continuous Growth (Advanced Concepts)",
icon: <Feather size={18} />,
terms: [
DEFINITIONS.Riverpod, DEFINITIONS.BLoC, DEFINITIONS.WidgetTesting,
DEFINITIONS.UnitTesting, DEFINITIONS.DevTools, DEFINITIONS.Key,
]
},
];

const DICTIONARY_MODES = {
FREQUENCY: 'Frequency',
ROADMAP: 'Roadmap'
};

// — SRS ALGORITHM —
const calculateNextReview = (easeFactor, interval, quality) => {
// quality: 0 = wrong, 1 = hard, 2 = good, 3 = easy (we’ll use 0 or 3)
let newEaseFactor = Math.max(1.3, easeFactor + (0.1 - (3 - quality) * (0.08 + (3 - quality) * 0.02)));

let newInterval;
if (quality < 2) { // Wrong answer
newInterval = 1; // Review tomorrow
} else if (interval === 0) {
newInterval = 1;
} else if (interval === 1) {
newInterval = 6;
} else {
newInterval = Math.round(interval * newEaseFactor);
}

const nextReview = Date.now() + (newInterval * 24 * 60 * 60 * 1000);
return { newEaseFactor, newInterval, nextReview };
};

// — AI GENERATOR HOOK —
const useAIGenerator = () => {
const [aiExample, setAiExample] = useState(null);
const [isLoading, setIsLoading] = useState(false);
const [error, setError] = useState(null);

// 1. & 2. Removed API Key and old API URL.
// 3. Implemented new worker URL.
const WORKER_URL = "https://psychic-waiter2.bradyyaddof.workers.dev/";

const generateContent = useCallback(async (conceptTerm, isHint) => {
setIsLoading(true);
setError(null);
setAiExample(null);

// This is the core instruction for the AI model
const type = isHint ? "a detailed, highly memorable mnemonic hint and explanation" : "a simple, beginner-friendly, and complete Dart or Flutter code snippet";
const systemPrompt = `You are an expert Dart/Flutter tutor. Your task is to generate ${type} for the user-provided concept.`;

let userQuery;
if (isHint) {
  userQuery = `Generate a detailed, helpful, and highly memorable hint for the concept: ${conceptTerm}. Start with a creative memory hook (1-2 sentences), followed by a brief, plain-text explanation of why it's critical for beginners (2-3 sentences). DO NOT use a code block or markdown formatting.`;
} else {
  userQuery = `Generate a working code example for the concept: ${conceptTerm}. The output MUST be only the code, enclosed in a single markdown block (either \`\`\`dart or \`\`\`jsx for Flutter widget code). DO NOT include any explanatory text outside the code block.`;
}

// 4. Standard Gemini API format for the request body (contents property is key)
const payload = {
  contents: [{ parts: [{ text: userQuery }] }],
  systemInstruction: { parts: [{ text: systemPrompt }] },
};

for (let attempt = 0; attempt < 3; attempt++) {
  try {
    // Direct fetch call to the new Cloudflare Worker URL
    const response = await fetch(WORKER_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload) // Sending the standard Gemini API payload
    });

    if (!response.ok) {
      if (response.status === 429 && attempt < 2) {
        throw new Error("Rate limit exceeded. Retrying...");
      } else {
        // Fallback error for non-successful HTTP status
        let errorBody = await response.text();
        try {
          // Attempt to parse a more detailed error from JSON response
          const errorJson = JSON.parse(errorBody);
          errorBody = errorJson.error?.message || errorBody;
        } catch {}
        
        throw new Error(`Worker Error! Status: ${response.status}. Message: ${errorBody}`);
      }
    }

    const result = await response.json();
    // The response structure should be what the original Gemini API returns, 
    // passed through by the worker.
    const text = result.candidates?.[0]?.content?.parts?.[0]?.text;

    if (text) {
      setAiExample({ concept: conceptTerm, content: text, isHint });
      setIsLoading(false);
      return;
    } else {
      throw new Error("Received empty or malformed response from AI (check worker logs).");
    }
  } catch (err) {
    console.error(`Attempt ${attempt + 1} failed:`, err);
    if (attempt < 2) {
      await new Promise(resolve => setTimeout(resolve, Math.pow(2, attempt) * 1000));
    } else {
      setError("Failed to generate AI example after multiple retries. Please try again. Error: " + err.message);
      setIsLoading(false);
      return;
    }
  }
}

}, []);

const closeAIExample = () => {
setAiExample(null);
setError(null);
};

return { aiExample, isLoading, error, generateContent, closeAIExample };
};

// — TEXT TO SPEECH —
const useSpeech = () => {
const speak = useCallback((text) => {
if ('speechSynthesis' in window) {
window.speechSynthesis.cancel();
const utterance = new SpeechSynthesisUtterance(text);
utterance.rate = 0.9;
utterance.pitch = 1;
window.speechSynthesis.speak(utterance);
}
}, []);

return { speak };
};

// — UTILITY COMPONENTS & VIEWS —

const CardTemplate = ({ term, definition, onFlip, showBack, related, cardStats }) => {
const relatedConcepts = related?.map(r => cardStats[r]).filter(Boolean) || [];

return (
<div 
className="relative h-full flex flex-col justify-between p-6 bg-white dark:bg-gray-800 rounded-2xl shadow-xl transform transition-all duration-300 hover:shadow-2xl cursor-pointer ring-4 ring-purple-500/50"
onClick={onFlip}
>
<div className="flex-grow flex flex-col justify-center text-center">
<h3 className={`text-3xl font-extrabold transition-opacity duration-500 ${showBack ? 'opacity-30 text-gray-400' : 'opacity-100 text-purple-700 dark:text-purple-300'}`}>
{term}
</h3>
<div className={`mt-4 transition-opacity duration-500 ${showBack ? 'opacity-100' : 'opacity-0 h-0 overflow-hidden'}`}>
<p className="text-lg text-gray-800 dark:text-gray-100 font-medium">{definition}</p>

      {showBack && relatedConcepts.length > 0 && (
        <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-600">
          <p className="text-sm font-semibold text-purple-600 dark:text-purple-400 mb-2">Related Concepts:</p>
          <div className="flex flex-wrap gap-2 justify-center">
            {relatedConcepts.map((rc, i) => (
              <span key={i} className="text-xs bg-purple-100 dark:bg-purple-900 text-purple-700 dark:text-purple-300 px-2 py-1 rounded-full">
                {rc.term}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  </div>
  
  <div className="absolute top-4 right-4 text-sm font-semibold text-gray-500 dark:text-gray-400">
    {showBack ? 'Definition' : 'Term'}
  </div>
</div>
);
};

const DictionaryView = ({ generateContent, isLoading, aiExample, closeAIExample, error, cardStats, setCardStats, speak }) => {
const [selectedTerm, setSelectedTerm] = useState(null);
const [dictionaryMode, setDictionaryMode] = useState(DICTIONARY_MODES.FREQUENCY);
const [editingNote, setEditingNote] = useState(null);
const [noteText, setNoteText] = useState('');
const [exampleText, setExampleText] = useState('');

const concepts = dictionaryMode === DICTIONARY_MODES.FREQUENCY ? CONCEPTS_BY_FREQUENCY : CONCEPTS_BY_ROADMAP;

const handleGenerateCode = (term) => {
generateContent(term, false);
};

const handleSaveNote = (termKey) => {
setCardStats(prev => ({
...prev,
[termKey]: {
...prev[termKey],
personalNote: noteText,
personalExample: exampleText
}
}));
setEditingNote(null);
setNoteText('');
setExampleText('');
};

const handleEditNote = (termKey) => {
setEditingNote(termKey);
setNoteText(cardStats[termKey].personalNote || '');
setExampleText(cardStats[termKey].personalExample || '');
};

return (
<div className="p-4 md:p-8 bg-white dark:bg-gray-900 min-h-screen">
<h1 className="text-3xl font-extrabold text-gray-900 dark:text-white mb-4 flex items-center">
<BookOpen className="mr-3 text-purple-600" size={30} /> Flutter/Dart Dictionary
</h1>

  <div className="mb-6 flex space-x-3 items-center overflow-x-auto pb-2">
    <button
      onClick={() => setDictionaryMode(DICTIONARY_MODES.FREQUENCY)}
      className={`flex items-center p-2 text-sm rounded-lg transition-colors flex-shrink-0 ${
        dictionaryMode === DICTIONARY_MODES.FREQUENCY
          ? 'bg-purple-600 text-white shadow-md'
          : 'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-200 hover:bg-purple-100 dark:hover:bg-gray-600'
      }`}
    >
      <LayoutList size={16} className="mr-1" /> Usage Frequency
    </button>
    <button
      onClick={() => setDictionaryMode(DICTIONARY_MODES.ROADMAP)}
      className={`flex items-center p-2 text-sm rounded-lg transition-colors flex-shrink-0 ${
        dictionaryMode === DICTIONARY_MODES.ROADMAP
          ? 'bg-purple-600 text-white shadow-md'
          : 'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-200 hover:bg-purple-100 dark:hover:bg-gray-600'
      }`}
    >
      <Waypoints size={16} className="mr-1" /> 🎯 Roadmap
    </button>
  </div>

  <p className="text-gray-500 dark:text-gray-400 mb-8 text-sm border-l-4 border-purple-400 pl-3 py-1">
    {dictionaryMode === DICTIONARY_MODES.FREQUENCY
      ? "Organized from Top (Most Used/Critical) to Bottom (Least Used/Advanced). Focus on the top categories first!"
      : "Organized by the structured learning path from your Google Keep note (🎯 Complete Dart & Flutter Developer Career Roadmap). Learn step-by-step!"
    }
  </p>

  <div className="grid grid-cols-1 gap-8">
    {concepts.map((category) => (
      <div key={category.category} className="border border-gray-200 dark:border-gray-700 rounded-xl p-4 shadow-lg">
        <h2 className="text-xl font-bold pb-2 mb-4 text-gray-800 dark:text-gray-200 flex items-center">
          {category.icon}
          <span className="ml-2 border-b-2 border-purple-400">{category.category}</span>
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {category.terms.map((term, index) => {
            const termKey = Object.keys(DEFINITIONS).find(k => DEFINITIONS[k].term === term.term);
            const stats = cardStats[termKey];
            const isEditing = editingNote === termKey;
            
            return (
              <div
                key={index}
                className={`p-4 rounded-xl cursor-pointer transition-all duration-200 flex flex-col justify-between ${
                  selectedTerm?.term === term.term
                    ? 'bg-purple-100 dark:bg-purple-900 shadow-lg ring-2 ring-purple-500'
                    : 'bg-gray-50 dark:bg-gray-800 hover:bg-purple-50 dark:hover:bg-gray-700'
                }`}
                onClick={() => setSelectedTerm(selectedTerm?.term === term.term ? null : term)}
              >
                <div>
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="font-extrabold text-lg text-purple-700 dark:text-purple-300 flex-grow">{term.term}</h3>
                    <button
                      onClick={(e) => { e.stopPropagation(); speak(term.term); }}
                      className="ml-2 p-1 hover:bg-purple-200 dark:hover:bg-purple-800 rounded"
                      title="Pronounce term"
                    >
                      <Volume2 size={16} className="text-purple-600" />
                    </button>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{term.definition}</p>
                  
                  {stats?.personalNote && !isEditing && (
                    <div className="mt-2 p-2 bg-yellow-50 dark:bg-yellow-900/20 rounded text-xs">
                      <p className="font-semibold text-yellow-700 dark:text-yellow-300">📝 My Note:</p>
                      <p className="text-yellow-800 dark:text-yellow-200">{stats.personalNote}</p>
                    </div>
                  )}
                  
                  {stats?.personalExample && !isEditing && (
                    <div className="mt-2 p-2 bg-blue-50 dark:bg-blue-900/20 rounded text-xs">
                      <p className="font-semibold text-blue-700 dark:text-blue-300">💡 My Example:</p>
                      <p className="text-blue-800 dark:text-blue-200 font-mono">{stats.personalExample}</p>
                    </div>
                  )}
                  
                  {isEditing && (
                    <div className="mt-3 space-y-2" onClick={(e) => e.stopPropagation()}>
                      <textarea
                        value={noteText}
                        onChange={(e) => setNoteText(e.target.value)}
                        placeholder="Add your personal note..."
                        className="w-full p-2 text-xs border rounded dark:bg-gray-700 dark:border-gray-600"
                        rows={2}
                      />
                      <textarea
                        value={exampleText}
                        onChange={(e) => setExampleText(e.target.value)}
                        placeholder="Add your code example..."
                        className="w-full p-2 text-xs border rounded dark:bg-gray-700 dark:border-gray-600 font-mono"
                        rows={2}
                      />
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleSaveNote(termKey)}
                          className="flex-1 flex items-center justify-center p-2 text-xs font-bold text-white bg-green-500 hover:bg-green-600 rounded transition-colors"
                        >
                          <Save size={14} className="mr-1" /> Save
                        </button>
                        <button
                          onClick={() => setEditingNote(null)}
                          className="flex-1 flex items-center justify-center p-2 text-xs font-bold text-white bg-gray-500 hover:bg-gray-600 rounded transition-colors"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {selectedTerm?.term === term.term && !isEditing && (
                  <div className="mt-3 space-y-2">
                    <button
                      onClick={(e) => { e.stopPropagation(); handleGenerateCode(term.term); }}
                      disabled={isLoading}
                      className="w-full flex items-center justify-center p-2 text-xs font-bold text-white bg-green-500 hover:bg-green-600 rounded-lg transition-colors duration-200 disabled:opacity-50 shadow-md"
                    >
                      {isLoading ? <RefreshCw size={14} className="animate-spin mr-2" /> : <Zap size={14} className="mr-2" />}
                      {isLoading ? 'Generating...' : 'AI Code Example'}
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleEditNote(termKey); }}
                      className="w-full flex items-center justify-center p-2 text-xs font-bold text-white bg-blue-500 hover:bg-blue-600 rounded-lg transition-colors duration-200 shadow-md"
                    >
                      <Edit3 size={14} className="mr-2" /> Add Personal Note
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    ))}
  </div>

  <AIExampleModal 
    aiExample={aiExample} 
    closeAIExample={closeAIExample} 
    error={error}
  />
</div>
);
};

const FlashcardsView = ({ cardStats, updateCardStats, generateContent, isLoading, aiExample, closeAIExample, error, speak }) => {
  const [practiceMode, setPracticeMode] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showBack, setShowBack] = useState(false);
  const [scoreSelection, setScoreSelection] = useState(null);
  const [reverseMode, setReverseMode] = useState(false);
  const [shuffleMode, setShuffleMode] = useState(false);
  const [shuffledIndices, setShuffledIndices] = useState([]);

  const allTermsArray = useMemo(() => {
    const all = Object.values(cardStats);
    let filtered;
    
    if (practiceMode) {
      filtered = all.filter(stat => stat.nextReview <= Date.now() || (stat.wrongCount > 0 && stat.correctStreak < 3));
      filtered.sort((a, b) => b.wrongCount - a.wrongCount);
    } else {
      filtered = all;
    }
    
    return filtered;
  }, [cardStats, practiceMode]);

  useEffect(() => {
    if (shuffleMode && allTermsArray.length > 0) {
      const indices = allTermsArray.map((_, i) => i);
      for (let i = indices.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [indices[i], indices[j]] = [indices[j], indices[i]];
      }
      setShuffledIndices(indices);
      setCurrentIndex(0);
    } else {
      setShuffledIndices([]);
    }
  }, [shuffleMode, allTermsArray.length]);

  const displayIndex = shuffleMode && shuffledIndices.length > 0 ? shuffledIndices[currentIndex] : currentIndex;
  const totalCards = allTermsArray.length;
  const currentCard = allTermsArray[displayIndex];
  
  const wrongAnswerCount = Object.values(cardStats).filter(s => s.wrongCount > 0 && s.correctStreak < 3).length;
  const dueForReview = Object.values(cardStats).filter(s => s.nextReview <= Date.now()).length;

  const handleFlip = () => {
    if (!showBack) {
      setShowBack(true);
      setScoreSelection(null);
      closeAIExample();
      if (currentCard && !reverseMode) {
        speak(currentCard.definition);
      }
    }
  };

  const handleScore = (isCorrect) => {
    setScoreSelection(isCorrect ? 'correct' : 'wrong');
  };

  const handleGenerateHint = () => {
    generateContent(currentCard.term, true);
  };
  
  const handleNext = () => {
    if (!scoreSelection) return;

    updateCardStats(currentCard.term, scoreSelection === 'correct');

    setShowBack(false);
    setScoreSelection(null);
    closeAIExample();
    
    setTimeout(() => {
      const remainingTerms = Object.values(cardStats).filter(stat => stat.wrongCount > 0 && stat.correctStreak < 3);
      const currentListSize = practiceMode ? remainingTerms.length : Object.keys(cardStats).length;

      if (currentListSize === 0) {
        setCurrentIndex(0);
        return;
      }

      let nextIndex = currentIndex;
      if (practiceMode) {
        nextIndex = currentIndex >= currentListSize ? 0 : currentIndex;
      } else {
        nextIndex = (currentIndex + 1) % currentListSize;
      }
      setCurrentIndex(nextIndex);
    }, 50);
  };

  const handlePrev = () => {
    setShowBack(false);
    setScoreSelection(null);
    closeAIExample();
    setCurrentIndex((prev) => (prev - 1 + totalCards) % totalCards);
  };

  if (totalCards === 0 && practiceMode) {
    return (
      <div className="p-4 md:p-8 flex flex-col items-center justify-center min-h-screen">
        <h1 className="text-3xl font-extrabold text-gray-900 dark:text-white mb-6">Flashcard Session</h1>
        <div className="w-full max-w-lg h-[400px] flex flex-col items-center justify-center bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8">
          <Target size={48} className="text-green-500 mb-4" />
          <h2 className="text-xl font-bold mb-2">All Practice Cards Mastered!</h2>
          <p className="text-gray-600 dark:text-gray-400 text-center">
            You have mastered all concepts in Practice Mode. Great job!
          </p>
          <p className="mt-4 text-sm text-purple-500 cursor-pointer hover:underline" onClick={() => setPracticeMode(false)}>
            Click here to review all cards in Full Mode.
          </p>
        </div>
        <div className="mt-8 w-full max-w-lg">
          <PracticeModeToggle practiceMode={practiceMode} setPracticeMode={setPracticeMode} wrongAnswerCount={wrongAnswerCount} dueForReview={dueForReview} />
        </div>
      </div>
    );
  } else if (totalCards === 0) {
    return (
      <div className="p-4 md:p-8 flex flex-col items-center justify-center min-h-screen">
        <h1 className="text-3xl font-extrabold text-gray-900 dark:text-white mb-6">Flashcard Session</h1>
        <div className="w-full max-w-lg h-[400px] flex flex-col items-center justify-center bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8">
          <Target size={48} className="text-green-500 mb-4" />
          <h2 className="text-xl font-bold mb-2">No Cards to Display</h2>
          <p className="text-gray-600 dark:text-gray-400 text-center">
            The list of concepts may be empty. Please ensure the data structure is correctly loaded.
          </p>
        </div>
      </div>
    );
  }

  const displayTerm = reverseMode ? currentCard?.definition : currentCard?.term;
  const displayDefinition = reverseMode ? currentCard?.term : currentCard?.definition;

  return (
    <div className="p-4 md:p-8 flex flex-col items-center min-h-screen bg-gray-50 dark:bg-gray-900">
      <h1 className="text-3xl font-extrabold text-gray-900 dark:text-white mb-6 flex items-center">
        <Layers className="mr-3 text-purple-600" size={30} /> Flashcard Session
      </h1>
      
      <PracticeModeToggle practiceMode={practiceMode} setPracticeMode={setPracticeMode} wrongAnswerCount={wrongAnswerCount} dueForReview={dueForReview} />

      <div className="flex gap-2 mt-4 mb-4 w-full max-w-lg">
        <button
          onClick={() => setReverseMode(!reverseMode)}
          className={`flex-1 p-2 rounded-lg text-sm font-semibold transition-colors ${
            reverseMode ? 'bg-indigo-600 text-white' : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200'
          }`}
        >
          <RotateCcw size={16} className="inline mr-1" /> {reverseMode ? 'Definition → Term' : 'Term → Definition'}
        </button>
        <button
          onClick={() => setShuffleMode(!shuffleMode)}
          className={`flex-1 p-2 rounded-lg text-sm font-semibold transition-colors ${
            shuffleMode ? 'bg-orange-600 text-white' : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200'
          }`}
        >
          <Shuffle size={16} className="inline mr-1" /> {shuffleMode ? 'Shuffled' : 'Sequential'}
        </button>
      </div>

      <div className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-4">
        Card {currentIndex + 1} of {totalCards} - Errors: {currentCard?.wrongCount} | Streak: {currentCard?.correctStreak} | Reviews: {currentCard?.reviewCount}
      </div>
      
      <div className="w-full max-w-lg h-[400px] mb-6">
        {currentCard ? (
          <CardTemplate
            term={displayTerm}
            definition={displayDefinition}
            showBack={showBack}
            onFlip={!showBack ? handleFlip : undefined}
            related={currentCard.related}
            cardStats={cardStats}
          />
        ) : (
          <div className="h-full flex items-center justify-center bg-white dark:bg-gray-800 rounded-2xl shadow-xl">
            <p className="text-gray-500">No cards in this mode.</p>
          </div>
        )}
      </div>
      
      <div className="w-full max-w-lg flex flex-col items-center">
        {!showBack && (
          <button 
            onClick={handleFlip}
            className="w-full p-4 mb-4 bg-purple-600 text-white rounded-xl text-lg font-bold hover:bg-purple-700 transition-colors shadow-lg"
          >
            Flip Card to Reveal {reverseMode ? 'Term' : 'Definition'}
          </button>
        )}

        {showBack && (
          <>
            <div className="flex flex-wrap justify-center w-full space-x-2 sm:space-x-4 mb-4">
              <button 
                onClick={() => handleScore(true)}
                className={`p-3 flex items-center rounded-xl font-bold transition-all flex-grow sm:flex-grow-0 mb-2 sm:mb-0 ${
                  scoreSelection === 'correct' ? 'bg-green-600 text-white shadow-xl' : 'bg-green-100 text-green-700 hover:bg-green-200 dark:bg-green-900 dark:text-green-300'
                }`}
              >
                <Check size={20} className="mr-2" /> Correct (Yes)
              </button>
              <button 
                onClick={() => handleScore(false)}
                className={`p-3 flex items-center rounded-xl font-bold transition-all flex-grow sm:flex-grow-0 ${
                  scoreSelection === 'wrong' ? 'bg-red-600 text-white shadow-xl' : 'bg-red-100 text-red-700 hover:bg-red-200 dark:bg-red-900 dark:text-red-300'
                }`}
              >
                <X size={20} className="mr-2" /> Wrong (No)
              </button>
            </div>
            
            {scoreSelection === 'wrong' && (
              <button
                onClick={handleGenerateHint}
                disabled={isLoading}
                className="mb-4 w-full flex items-center justify-center p-3 text-sm font-bold text-white bg-indigo-500 hover:bg-indigo-600 rounded-xl transition-colors duration-200 disabled:opacity-50 shadow-md"
              >
                {isLoading ? <RefreshCw size={18} className="animate-spin mr-2" /> : <Zap size={18} className="mr-2" />} 
                {isLoading ? 'Generating Hint...' : 'Optional: Get AI Memorable Hint'}
              </button>
            )}

            <div className="flex justify-center items-center w-full space-x-3 mt-2">
              <button 
                onClick={handlePrev}
                className="p-3 bg-gray-300 text-gray-700 rounded-full hover:bg-gray-400 transition-colors shadow-md disabled:opacity-50"
                aria-label="Previous card"
              >
                <ChevronLeft size={24} />
              </button>

              <button
                onClick={handleNext}
                disabled={!scoreSelection}
                className="flex-grow p-3 bg-purple-600 text-white rounded-xl text-lg font-bold hover:bg-purple-700 transition-colors shadow-lg disabled:opacity-50 disabled:bg-gray-500"
              >
                <div className="flex items-center justify-center">
                  Next Card <SkipForward size={20} className="ml-2" />
                </div>
              </button>

              <button 
                onClick={handleNext}
                disabled={!scoreSelection}
                className="p-3 bg-gray-300 text-gray-700 rounded-full hover:bg-gray-400 transition-colors shadow-md disabled:opacity-50"
                aria-label="Next card"
              >
                <ChevronRight size={24} />
              </button>
            </div>
          </>
        )}
      </div>

      <AIExampleModal 
        aiExample={aiExample} 
        closeAIExample={closeAIExample} 
        error={error}
      />
    </div>
  );
};

const ChallengeView = ({ cardStats, updateCardStats }) => {
  const [gameState, setGameState] = useState('setup'); // setup, playing, finished
  const [challengeType, setChallengeType] = useState('match'); // match, cloze
  const [questionCount, setQuestionCount] = useState(10);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [questions, setQuestions] = useState([]);
  const [score, setScore] = useState(0);
  const [timeLeft, setTimeLeft] = useState(60);
  const [selectedAnswer, setSelectedAnswer] = useState(null);

  useEffect(() => {
    let timer;
    if (gameState === 'playing' && timeLeft > 0) {
      timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
    } else if (timeLeft === 0 && gameState === 'playing') {
      handleFinish();
    }
    return () => clearTimeout(timer);
  }, [timeLeft, gameState]);

  const startChallenge = () => {
    const allCards = Object.values(cardStats);
    const shuffled = [...allCards].sort(() => Math.random() - 0.5).slice(0, questionCount);
    
    const generatedQuestions = shuffled.map(card => {
      const wrongOptions = Object.values(cardStats)
        .filter(c => c.term !== card.term)
        .sort(() => Math.random() - 0.5)
        .slice(0, 3)
        .map(c => c.definition);
      
      const options = [card.definition, ...wrongOptions].sort(() => Math.random() - 0.5);
      
      return {
        term: card.term,
        correctAnswer: card.definition,
        options: options,
        termKey: Object.keys(cardStats).find(k => cardStats[k].term === card.term)
      };
    });

    setQuestions(generatedQuestions);
    setGameState('playing');
    setCurrentQuestion(0);
    setScore(0);
    setTimeLeft(questionCount * 15); // 15 seconds per question
    setSelectedAnswer(null);
  };

  const handleAnswer = (answer) => {
    setSelectedAnswer(answer);
    const correct = answer === questions[currentQuestion].correctAnswer;
    
    if (correct) {
      setScore(score + 1);
      updateCardStats(questions[currentQuestion].termKey, true);
    } else {
      updateCardStats(questions[currentQuestion].termKey, false);
    }

    setTimeout(() => {
      if (currentQuestion + 1 < questions.length) {
        setCurrentQuestion(currentQuestion + 1);
        setSelectedAnswer(null);
      } else {
        handleFinish();
      }
    }, 1000);
  };

  const handleFinish = () => {
    setGameState('finished');
  };

  if (gameState === 'setup') {
    return (
      <div className="p-4 md:p-8 flex flex-col items-center min-h-screen bg-gradient-to-br from-purple-50 to-blue-50 dark:from-gray-900 dark:to-gray-800">
        <h1 className="text-4xl font-extrabold text-gray-900 dark:text-white mb-6 flex items-center">
          <Trophy className="mr-3 text-yellow-500" size={36} /> Challenge Mode
        </h1>
        
        <div className="w-full max-w-lg bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-8">
          <h2 className="text-2xl font-bold mb-6 text-purple-700 dark:text-purple-300">Setup Your Challenge</h2>
          
          <div className="mb-6">
            <label className="block text-sm font-semibold mb-2 text-gray-700 dark:text-gray-300">Number of Questions</label>
            <select
              value={questionCount}
              onChange={(e) => setQuestionCount(Number(e.target.value))}
              className="w-full p-3 border rounded-lg dark:bg-gray-700 dark:border-gray-600 text-lg"
            >
              <option value={5}>5 Questions</option>
              <option value={10}>10 Questions</option>
              <option value={15}>15 Questions</option>
              <option value={20}>20 Questions</option>
            </select>
          </div>

          <button
            onClick={startChallenge}
            className="w-full p-4 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-xl text-xl font-bold hover:from-purple-700 hover:to-blue-700 transition-all shadow-lg transform hover:scale-105"
          >
            <Play size={24} className="inline mr-2" /> Start Challenge!
          </button>
        </div>
      </div>
    );
  }

  if (gameState === 'finished') {
    const percentage = Math.round((score / questions.length) * 100);
    const passed = percentage >= 70;
    
    return (
      <div className="p-4 md:p-8 flex flex-col items-center min-h-screen bg-gradient-to-br from-purple-50 to-blue-50 dark:from-gray-900 dark:to-gray-800">
        <h1 className="text-4xl font-extrabold text-gray-900 dark:text-white mb-6">Challenge Complete!</h1>
        
        <div className="w-full max-w-lg bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-8 text-center">
          <div className={`text-6xl mb-4 ${passed ? 'text-green-500' : 'text-orange-500'}`}>
            {passed ? '🎉' : '💪'}
          </div>
          
          <h2 className="text-3xl font-bold mb-4">{passed ? 'Excellent Work!' : 'Keep Practicing!'}</h2>
          
          <div className="text-5xl font-extrabold text-purple-600 dark:text-purple-400 mb-2">
            {score} / {questions.length}
          </div>
          <div className="text-2xl text-gray-600 dark:text-gray-400 mb-6">
            {percentage}% Correct
          </div>
          
          <div className="flex gap-3">
            <button
              onClick={() => setGameState('setup')}
              className="flex-1 p-3 bg-purple-600 text-white rounded-xl font-bold hover:bg-purple-700 transition-colors"
            >
              <RotateCcw size={20} className="inline mr-2" /> Try Again
            </button>
            <button
              onClick={() => window.location.reload()}
              className="flex-1 p-3 bg-gray-600 text-white rounded-xl font-bold hover:bg-gray-700 transition-colors"
            >
              Return Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  const question = questions[currentQuestion];
  const progress = ((currentQuestion + 1) / questions.length) * 100;

  return (
    <div className="p-4 md:p-8 flex flex-col items-center min-h-screen bg-gradient-to-br from-purple-50 to-blue-50 dark:from-gray-900 dark:to-gray-800">
      <div className="w-full max-w-lg mb-4 flex justify-between items-center">
        <div className="text-sm font-semibold text-gray-700 dark:text-gray-300">
          Question {currentQuestion + 1} / {questions.length}
        </div>
        <div className="flex items-center text-sm font-semibold text-red-600 dark:text-red-400">
          <Clock size={18} className="mr-1" /> {timeLeft}s
        </div>
      </div>

      <div className="w-full max-w-lg mb-6 bg-gray-200 dark:bg-gray-700 rounded-full h-3">
        <div
          className="bg-gradient-to-r from-purple-600 to-blue-600 h-3 rounded-full transition-all duration-300"
          style={{ width: `${progress}%` }}
        />
      </div>

      <div className="w-full max-w-lg bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-8">
        <h2 className="text-2xl font-bold mb-6 text-center text-purple-700 dark:text-purple-300">
          What is: <span className="text-gray-900 dark:text-white">{question.term}</span>?
        </h2>

        <div className="space-y-3">
          {question.options.map((option, index) => {
            let bgColor = 'bg-gray-100 dark:bg-gray-700 hover:bg-purple-100 dark:hover:bg-purple-900';
            if (selectedAnswer) {
              if (option === question.correctAnswer) {
                bgColor = 'bg-green-500 text-white';
              } else if (option === selectedAnswer) {
                bgColor = 'bg-red-500 text-white';
              }
            }

            return (
              <button
                key={index}
                onClick={() => !selectedAnswer && handleAnswer(option)}
                disabled={selectedAnswer !== null}
                className={`w-full p-4 rounded-xl text-left font-medium transition-all ${bgColor} ${
                  selectedAnswer ? 'cursor-not-allowed' : 'cursor-pointer transform hover:scale-102'
                }`}
              >
                {option}
              </button>
            );
          })}
        </div>

        <div className="mt-6 text-center text-lg font-semibold text-purple-600 dark:text-purple-400">
          Score: {score} / {currentQuestion + (selectedAnswer ? 1 : 0)}
        </div>
      </div>
    </div>
  );
};

const ProgressView = ({ cardStats, studyHistory }) => {
  const stats = useMemo(() => {
    const allCards = Object.values(cardStats);
    const totalCards = allCards.length;
    const masteredCards = allCards.filter(c => c.correctStreak >= 3).length;
    const strugglingCards = allCards.filter(c => c.wrongCount > 0 && c.correctStreak < 3).length;
    const dueCards = allCards.filter(c => c.nextReview <= Date.now()).length;
    const totalReviews = allCards.reduce((sum, c) => sum + c.reviewCount, 0);
    
    const categoryStats = {};
    CONCEPTS_BY_FREQUENCY.forEach(cat => {
      const catTerms = cat.terms.map(t => Object.keys(DEFINITIONS).find(k => DEFINITIONS[k].term === t.term));
      const catMastered = catTerms.filter(t => cardStats[t]?.correctStreak >= 3).length;
      categoryStats[cat.category] = {
        total: catTerms.length,
        mastered: catMastered,
        percentage: Math.round((catMastered / catTerms.length) * 100)
      };
    });

    const last7Days = studyHistory.slice(-7);
    const dailyReviews = last7Days.map(day => day.reviews);

    return {
      totalCards,
      masteredCards,
      strugglingCards,
      dueCards,
      totalReviews,
      masteryPercentage: Math.round((masteredCards / totalCards) * 100),
      categoryStats,
      dailyReviews,
      currentStreak: studyHistory.filter(d => d.studied).length
    };
  }, [cardStats, studyHistory]);

  return (
    <div className="p-4 md:p-8 bg-white dark:bg-gray-900 min-h-screen">
      <h1 className="text-3xl font-extrabold text-gray-900 dark:text-white mb-6 flex items-center">
        <BarChart3 className="mr-3 text-purple-600" size={30} /> Progress Dashboard
      </h1>

      {/* Overall Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-6 text-white shadow-lg">
          <div className="flex items-center justify-between mb-2">
            <Award size={24} />
            <span className="text-3xl font-extrabold">{stats.masteredCards}</span>
          </div>
          <div className="text-sm font-semibold opacity-90">Mastered</div>
          <div className="text-xs opacity-75">{stats.masteryPercentage}% of total</div>
        </div>

        <div className="bg-gradient-to-br from-red-500 to-red-600 rounded-xl p-6 text-white shadow-lg">
          <div className="flex items-center justify-between mb-2">
            <Target size={24} />
            <span className="text-3xl font-extrabold">{stats.strugglingCards}</span>
          </div>
          <div className="text-sm font-semibold opacity-90">Struggling</div>
          <div className="text-xs opacity-75">Need practice</div>
        </div>

        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-6 text-white shadow-lg">
          <div className="flex items-center justify-between mb-2">
            <Calendar size={24} />
            <span className="text-3xl font-extrabold">{stats.dueCards}</span>
          </div>
          <div className="text-sm font-semibold opacity-90">Due Today</div>
          <div className="text-xs opacity-75">Ready to review</div>
        </div>

        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-6 text-white shadow-lg">
          <div className="flex items-center justify-between mb-2">
            <Flame size={24} />
            <span className="text-3xl font-extrabold">{stats.currentStreak}</span>
          </div>
          <div className="text-sm font-semibold opacity-90">Day Streak</div>
          <div className="text-xs opacity-75">Keep it up!</div>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg mb-8">
        <h2 className="text-xl font-bold mb-4 flex items-center">
          <TrendingUp size={20} className="mr-2 text-purple-600" /> Overall Mastery
        </h2>
        <div className="relative">
          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-8">
            <div
              className="bg-gradient-to-r from-purple-600 to-blue-600 h-8 rounded-full flex items-center justify-center text-white font-bold transition-all duration-500"
              style={{ width: `${stats.masteryPercentage}%` }}
            >
              {stats.masteryPercentage}%
            </div>
          </div>
          <div className="mt-2 text-sm text-gray-600 dark:text-gray-400 text-center">
            {stats.masteredCards} of {stats.totalCards} conceptsmastered
          </div>
        </div>
      </div>

      {/* Category Breakdown */}
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg mb-8">
        <h2 className="text-xl font-bold mb-4 flex items-center">
          <Layers size={20} className="mr-2 text-purple-600" /> Category Progress
        </h2>
        <div className="space-y-4">
          {Object.entries(stats.categoryStats).map(([category, data]) => (
            <div key={category}>
              <div className="flex justify-between mb-1 text-sm">
                <span className="font-semibold text-gray-700 dark:text-gray-300">{category}</span>
                <span className="text-gray-600 dark:text-gray-400">{data.mastered}/{data.total}</span>
              </div>
              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3">
                <div
                  className="bg-purple-600 h-3 rounded-full transition-all duration-500"
                  style={{ width: `${data.percentage}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Study Activity */}
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg">
        <h2 className="text-xl font-bold mb-4 flex items-center">
          <Calendar size={20} className="mr-2 text-purple-600" /> Last 7 Days Activity
        </h2>
        <div className="flex items-end justify-between h-32 gap-2">
          {stats.dailyReviews.map((reviews, index) => {
            const maxReviews = Math.max(...stats.dailyReviews, 1);
            const height = (reviews / maxReviews) * 100;
            return (
              <div key={index} className="flex-1 flex flex-col items-center">
                <div className="w-full bg-purple-600 rounded-t transition-all duration-300 hover:bg-purple-700" style={{ height: `${height}%`, minHeight: '4px' }} />
                <div className="text-xs mt-2 text-gray-600 dark:text-gray-400">{reviews}</div>
              </div>
            );
          })}
        </div>
        <div className="mt-4 text-center text-sm text-gray-600 dark:text-gray-400">
          Total Reviews: {stats.totalReviews}
        </div>
      </div>
    </div>
  );
};

const PracticeModeToggle = ({ practiceMode, setPracticeMode, wrongAnswerCount, dueForReview }) => (
  <div 
    className={`flex items-center p-2 rounded-xl cursor-pointer transition-all duration-300 w-full max-w-lg shadow-inner ${
      practiceMode ? 'bg-red-100 dark:bg-red-900' : 'bg-gray-200 dark:bg-gray-700'
    }`}
    onClick={() => setPracticeMode(!practiceMode)}
  >
    <div className={`p-2 rounded-full ${practiceMode ? 'bg-red-500 text-white' : 'bg-gray-400 text-gray-100'}`}>
      <Target size={20} />
    </div>
    <div className="ml-3 flex-grow">
      <h3 className="font-bold text-sm text-gray-800 dark:text-gray-100">Practice Mode</h3>
      <p className="text-xs text-gray-600 dark:text-gray-300">
        {practiceMode 
          ? `FOCUSED: ${wrongAnswerCount} struggling + ${dueForReview} due for review`
          : `FULL REVIEW: All concepts (${wrongAnswerCount} errors, ${dueForReview} due)`
        }
      </p>
    </div>
  </div>
);

const AIExampleModal = ({ aiExample, closeAIExample, error }) => {
  const [showDartPad, setShowDartPad] = useState(false);
  
  if (!aiExample && !error) return null;

  const contentBlock = aiExample?.content || (error ? `Error: ${error}` : 'Generating...');
  const isHint = aiExample?.isHint;

  // Extract code from markdown blocks
  const codeMatch = contentBlock.match(/```(?:dart|jsx|javascript)?\n([\s\S]*?)```/);
  const hasCode = codeMatch && !isHint;
  const codeContent = hasCode ? codeMatch[1] : '';
  
  // Check if desktop (not perfect but reasonable heuristic)
  const isDesktop = typeof window !== 'undefined' && window.innerWidth >= 1024;

  const ModalHeader = () => (
    <h2 className={`text-xl font-bold ${isHint ? 'text-indigo-600 dark:text-indigo-400' : 'text-purple-600 dark:text-purple-400'} mb-3 flex items-center`}>
      {isHint ? <Feather size={24} className="mr-2" /> : <Zap size={24} className="mr-2" />} 
      {isHint ? 'AI Memorable Hint' : 'AI Code Example'}: 
      <span className="text-gray-900 dark:text-white ml-2">{aiExample?.concept || "Error"}</span>
    </h2>
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto shadow-2xl p-6 relative">
        <button 
          onClick={closeAIExample}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors text-2xl font-bold z-10"
          aria-label="Close"
        >
          &times;
        </button>
        
        <ModalHeader />
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
          {isHint 
            ? "This hint is designed to be highly memorable for long-term recall."
            : "This code snippet helps you understand the concept in context."
          }
        </p>
        
        <div className={`rounded-lg p-3 text-sm overflow-x-auto whitespace-pre-wrap ${isHint ? 'bg-yellow-50 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200 italic font-sans' : 'bg-gray-100 dark:bg-gray-900 font-mono'}`}>
          {contentBlock}
        </div>
        
        {hasCode && isDesktop && (
          <div className="mt-4">
            <button
              onClick={() => setShowDartPad(!showDartPad)}
              className="w-full p-3 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 transition-colors flex items-center justify-center"
            >
              <Play size={18} className="mr-2" /> {showDartPad ? 'Hide' : 'Try in'} DartPad
            </button>
            
            {showDartPad && (
              <div className="mt-4">
                <iframe
                  src={`https://dartpad.dev/embed-dart.html?id=&split=60&theme=dark`}
                  style={{ width: '100%', height: '500px', border: 'none' }}
                  title="DartPad"
                  className="rounded-lg"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                  Copy the code above and paste it into DartPad to run it.
                </p>
              </div>
            )}
          </div>
        )}
        
        <button
          onClick={closeAIExample}
          className="mt-6 w-full p-3 text-white bg-purple-600 hover:bg-purple-700 rounded-xl font-semibold transition-colors shadow-lg"
        >
          Got It! Close {isHint ? 'Hint' : 'Example'}
        </button>
      </div>
    </div>
  );
};

// --- MAIN APP COMPONENT ---

const App = () => {
  const [currentTab, setCurrentTab] = useState('Dictionary');
  const [cardStats, setCardStats] = useStorageState('cardStats', getInitialCardStats());
  const [studyHistory, setStudyHistory] = useStorageState('studyHistory', []);
  const [dailyGoal, setDailyGoal] = useStorageState('dailyGoal', 10);
  const { aiExample, isLoading, error, generateContent, closeAIExample } = useAIGenerator();
  const { speak } = useSpeech();
  
  // Track daily study
  useEffect(() => {
    const today = new Date().toDateString();
    const lastEntry = studyHistory[studyHistory.length - 1];
    
    if (!lastEntry || lastEntry.date !== today) {
      setStudyHistory([...studyHistory, { date: today, reviews: 0, studied: false }]);
    }
  }, []);

  const updateCardStats = useCallback((termKey, isCorrect) => {
    setCardStats(prevStats => {
      const current = prevStats[termKey];
      if (!current) return prevStats;

      const quality = isCorrect ? 3 : 0;
      const currentInterval = current.reviewCount === 0 ? 0 : 
        Math.round((current.nextReview - Date.now()) / (24 * 60 * 60 * 1000));
      
      const { newEaseFactor, newInterval, nextReview } = calculateNextReview(
        current.easeFactor,
        Math.max(0, currentInterval),
        quality
      );

      if (isCorrect) {
        const newStreak = current.correctStreak + 1;
        let newWrongCount = current.wrongCount;
        
        if (newStreak >= 3) {
          newWrongCount = 0;
        }

        return {
          ...prevStats,
          [termKey]: {
            ...current,
            wrongCount: newWrongCount,
            correctStreak: newStreak,
            lastReviewed: Date.now(),
            nextReview: nextReview,
            reviewCount: current.reviewCount + 1,
            easeFactor: newEaseFactor,
          }
        };
      } else {
        return {
          ...prevStats,
          [termKey]: {
            ...current,
            wrongCount: current.wrongCount + 1,
            correctStreak: 0,
            lastReviewed: Date.now(),
            nextReview: nextReview,
            reviewCount: current.reviewCount + 1,
            easeFactor: newEaseFactor,
          }
        };
      }
    });

    // Update study history
    setStudyHistory(prev => {
      const newHistory = [...prev];
      const today = new Date().toDateString();
      const todayIndex = newHistory.findIndex(h => h.date === today);
      
      if (todayIndex >= 0) {
        newHistory[todayIndex] = {
          ...newHistory[todayIndex],
          reviews: newHistory[todayIndex].reviews + 1,
          studied: true
        };
      }
      
      return newHistory;
    });
  }, [setCardStats, setStudyHistory]);

  const exportProgress = () => {
    const data = {
      cardStats,
      studyHistory,
      dailyGoal,
      exportDate: new Date().toISOString()
    };
    
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `flutter-learning-progress-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const resetProgress = () => {
    if (window.confirm('Are you sure you want to reset all progress? This cannot be undone.')) {
      setCardStats(getInitialCardStats());
      setStudyHistory([]);
    }
  };

  const tabs = [
    { id: 'Dictionary', icon: BookOpen, label: 'Dictionary' },
    { id: 'Flashcards', icon: Layers, label: 'Flashcards' },
    { id: 'Challenge', icon: Trophy, label: 'Challenge' },
    { id: 'Progress', icon: BarChart3, label: 'Progress' }
  ];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white font-sans">
      <style dangerouslySetInnerHTML={{__html: `
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@100..900&display=swap');
        body { font-family: 'Inter', sans-serif; }
        .tab-active { @apply border-b-4 border-purple-500 text-purple-700 font-bold dark:border-purple-400 dark:text-purple-300; }
        .font-sans { word-break: break-word; }
      `}} />

      <header className="sticky top-0 z-10 bg-white dark:bg-gray-800 shadow-md">
        <div className="max-w-6xl mx-auto flex justify-between items-center p-3 overflow-x-auto">
          <div className="flex space-x-2 sm:space-x-4">
            {tabs.map(tab => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => { setCurrentTab(tab.id); closeAIExample(); }}
                  className={`p-2 transition-all duration-300 text-sm sm:text-base flex items-center whitespace-nowrap ${currentTab === tab.id ? 'tab-active' : 'text-gray-500 hover:text-purple-500'}`}
                >
                  <Icon size={18} className="mr-1" /> {tab.label}
                </button>
              );
            })}
          </div>
          
          <div className="flex items-center space-x-2">
            <button
              onClick={exportProgress}
              className="p-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors text-sm"
              title="Export Progress"
            >
              💾
            </button>
            <button
              onClick={resetProgress}
              className="p-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors text-sm"
              title="Reset Progress"
            >
              <RotateCcw size={16} />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto">
        {currentTab === 'Dictionary' && (
          <DictionaryView 
            generateContent={generateContent}
            isLoading={isLoading}
            aiExample={aiExample}
            closeAIExample={closeAIExample}
            error={error}
            cardStats={cardStats}
            setCardStats={setCardStats}
            speak={speak}
          />
        )}
        
        {currentTab === 'Flashcards' && (
          <FlashcardsView 
            cardStats={cardStats}
            updateCardStats={updateCardStats}
            generateContent={generateContent}
            isLoading={isLoading}
            aiExample={aiExample}
            closeAIExample={closeAIExample}
            error={error}
            speak={speak}
          />
        )}
        
        {currentTab === 'Challenge' && (
          <ChallengeView 
            cardStats={cardStats}
            updateCardStats={updateCardStats}
          />
        )}
        
        {currentTab === 'Progress' && (
          <ProgressView 
            cardStats={cardStats}
            studyHistory={studyHistory}
          />
        )}
      </main>
    </div>
  );
};

export default App;
